import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminRoutes } from "../../../../routers/AdminRouters";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  LicensesType,
  PlanVariant,
  PlanBillingCycle,
} from "../../../../types/subscription/subscriptionScreen/subscriptionScreen.type";
import {
  useGetOrganisationsQuery,
  useGetProfileQuery,
} from "../../../../services";
import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  useAddLicensesMutation,
  useGetPaymentDetailsQuery,
  useGetPlansQuery,
  useGetTaxesQuery,
} from "../../../../services/subscription";
import ConfirmSubscriptionModal from "./ConfirmSubscriptionModal";
import { selectOrganisationId } from "../../../../redux/organisationReducer";
import { loadRazorpayScript } from "../../../../utils/loadRazorpay";
import { subscriptionLicenseApi } from "../../../../services/subscription/subscriptionLicense.rtk.api";

interface AddScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onPaymentFailed?: () => void;
}

const AddLicensesModal: React.FC<AddScreenModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onPaymentFailed
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [billingCycle, setBillingCycle] = useState<PlanBillingCycle>(
    PlanBillingCycle.MONTHLY,
  );
  const [licenseType, setLicenseType] = useState<LicensesType>(
    LicensesType.BASIC,
  );
  const [licenseCount, setLicenseCount] = useState(1);
  const { admin, isLoggedIn, profile } = useAppSelector(
    (state: RootState) => state.admin,
  );
  const organisationId = useAppSelector(selectOrganisationId);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(organisationId || "");

  const { data: paymentDta } = useGetPaymentDetailsQuery({ organisationId: selectedOrgId || organisationId });
  const { data: profileResp } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: organisationsResp, isLoading: orgsLoading } =
    useGetOrganisationsQuery(undefined, {
      skip: !isLoggedIn,
    });

  // Fetch plans
  const { data: plansResp, isLoading: plansLoading } = useGetPlansQuery(
    undefined,
    {
      skip: !isLoggedIn,
    },
  );

  // Fetch taxes
  const { data: taxesResp, isLoading: taxesLoading } = useGetTaxesQuery(
    undefined,
    {
      skip: !isLoggedIn,
    },
  );

  const [addLicenses, { isLoading: adding }] = useAddLicensesMutation();

  const [showConfirm, setShowConfirm] = useState(false);

  const assignedOrgsRaw = (profileResp as any)?.result?.assignedOrganisations;
  const assignedOrgIdsObj: string[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw
      .map((o: any) => (typeof o === "string" ? o : o?._id))
      .filter(Boolean)
    : [];

  // Memoize to prevent exhaustive deps triggering hooks later on
  const assignedOrgIdsStr = assignedOrgIdsObj.join(",");
  const assignedOrgIds = React.useMemo(() => assignedOrgIdsObj, [assignedOrgIdsStr]);

  const assignedOrgsObjs: any[] = Array.isArray(assignedOrgsRaw)
    ? assignedOrgsRaw.filter((o: any) => typeof o === "object" && o)
    : [];

  const rawRole =
    (profile?.role as any)?.name ||
    profile?.role ||
    (profileResp as any)?.result?.role?.name ||
    (profileResp as any)?.result?.role ||
    "";

  const isSuperAdmin = String(rawRole).replace(/\s/g, "").toLowerCase() === "superadmin";

  const allOrgs: any[] = Array.isArray((organisationsResp as any)?.data)
    ? (organisationsResp as any).data
    : Array.isArray(organisationsResp)
      ? (organisationsResp as any[])
      : [];

  const filteredOrgs = isSuperAdmin
    ? allOrgs
    : allOrgs.filter((org) => assignedOrgIds.includes(org?._id));

  const options =
    filteredOrgs.length > 0
      ? filteredOrgs.map((org) => ({
        value: String(org?._id),
        label: String((org?.displayName || org?.name) ?? org?._id),
        currency: org?.currency ?? "",
      }))
      : assignedOrgsObjs.length > 0
        ? assignedOrgsObjs.map((org) => ({
          value: String(org?._id),
          label: String((org?.displayName || org?.name) ?? org?._id),
        }))
        : assignedOrgIds.map((id) => ({
          value: String(id),
          label: String(id),
        }));

  const getDefaultSelectedOrgId = () => {
    if (organisationId) return organisationId;
    if (assignedOrgIds.length > 0) return assignedOrgIds[0];
    return "";
  };

  useEffect(() => {
    if (organisationId && selectedOrgId !== organisationId && isOpen) {
      // Sync from header only if we just opened or if the header actually forcefully changed.
      // But let's avoid overriding if user manually changed it. 
      // Safest behavior when header changes while modal is open: update standard org.
      setSelectedOrgId(organisationId);
    } else if (!selectedOrgId && assignedOrgIds.length > 0) {
      setSelectedOrgId(assignedOrgIds[0]);
    }
  }, [organisationId]);

  const resetModalState = () => {
    setBillingCycle(PlanBillingCycle.MONTHLY);
    setLicenseType(LicensesType.BASIC);
    setLicenseCount(1);
    setSelectedOrgId(getDefaultSelectedOrgId());
    setShowConfirm(false);
  };

  const handleModalClose = () => {
    resetModalState();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    } else {
      setSelectedOrgId(getDefaultSelectedOrgId());
    }
  }, [isOpen]);

  const incrementCount = () => setLicenseCount((prev) => prev + 1);
  const decrementCount = () =>
    setLicenseCount((prev) => (prev > 1 ? prev - 1 : 1));

  const plans: any[] = Array.isArray((plansResp as any)?.results)
    ? (plansResp as any).results
    : Array.isArray((plansResp as any)?.data?.results)
      ? (plansResp as any).data.results
      : Array.isArray(plansResp)
        ? (plansResp as any[])
        : [];

  // Filter only license variant plans for the selected billing cycle
  const licensePlans = plans.filter(
    (p) =>
      p?.variant === PlanVariant.LICENSE &&
      String(p?.cycle)?.toUpperCase?.() === billingCycle,
  );

  const premiumPlan = licensePlans.find(
    (p) => String(p?.type)?.toUpperCase?.() === "PREMIUM",
  );
  const basicPlan = licensePlans.find(
    (p) => String(p?.type)?.toUpperCase?.() === "BASIC",
  );

  const activePlan =
    licenseType === LicensesType.PREMIUM ? premiumPlan : basicPlan;

  // If current licenseType has no matching plan for this cycle, switch to the available one
  useEffect(() => {
    const isPremiumDisabledEnv = import.meta.env.VITE_DISABLE_PREMIUM_PLANS === "YES";

    if (licenseType === LicensesType.PREMIUM && isPremiumDisabledEnv && basicPlan) {
      setLicenseType(LicensesType.BASIC);
    } else if (licenseType === LicensesType.BASIC && !basicPlan && premiumPlan && !isPremiumDisabledEnv) {
      setLicenseType(LicensesType.PREMIUM);
    } else if (
      licenseType === LicensesType.PREMIUM &&
      !premiumPlan &&
      basicPlan
    ) {
      setLicenseType(LicensesType.BASIC);
    }
  }, [licenseType, basicPlan, premiumPlan]);
  // Find selected org object to get currency
  const selectedOrgObj =
    filteredOrgs.find((org) => String(org?._id) === selectedOrgId) ||
    assignedOrgsObjs.find((org) => String(org?._id) === selectedOrgId);

  // Read provider from the nested gateway object
  const paymentProvider = selectedOrgObj?.gateway?.provider?.toLowerCase() || "stripe";
  const isRazorpay = paymentProvider === "razorpay";
  const needsCardFirst = !isRazorpay && paymentDta?.results?.length === 0;
  const trial = selectedOrgObj?.subscriber?.trial;
  let isTrialUsed = selectedOrgObj?.subscriber?.isTrialUsed;
  const isTrialEligible = selectedOrgObj?.subscriber?.isTrialEligible;
  const trialEndDate = selectedOrgObj?.subscriber?.trial?.endDate;
  const billingDate = selectedOrgObj?.billing?.nextBillingOn;
  const now = new Date();
  const trialRemainingDays = trialEndDate
    ? Math.max(
      0,
      Math.ceil(
        (new Date(trialEndDate).getTime() - now.getTime()) /
        (1000 * 60 * 60 * 24)
      )
    )
    : 0;

  // 👉 Billing days
  const billingRemainingDays = billingDate
    ? Math.max(
      0,
      Math.ceil(
        (new Date(billingDate).getTime() - now.getTime()) /
        (1000 * 60 * 60 * 24)
      )
    )
    : 0;


  // states
  const isTrialActive =
    trialEndDate && new Date(trialEndDate) > now;

  const isTrialExpired =
    trialEndDate && new Date(trialEndDate) <= now;

  let message = "";

  // 1. KILL SWITCH: If the trial is used up, completely ignore all trial messaging.
  if (isTrialUsed) {

  }
  // 2. ACTIVE TRIAL: They haven't used it up yet, and the date is in the future.
  else if (isTrialActive) {
    message = `Your free trial ends in ${trialRemainingDays} day${trialRemainingDays > 1 ? "s" : ""
      }.`;
  }
  // 3. ELIGIBLE: They haven't started a trial, but the backend says they can.
  else if (isTrialEligible) {
    message = "You are eligible for a free trial.";
  }
  // 4. FALLBACK: Catch-all for standard paying users just in case.


  useEffect(() => {
    if (isRazorpay) {
      setLicenseCount(1);
    }
  }, [isRazorpay]);
  const nextBillingAt =
    selectedOrgObj?.billing?.nextBillingOn
  const hasActiveSubscription =
    !!selectedOrgObj?.billing?.nextBillingOn;

  const selectedCurrency = selectedOrgObj?.currency?.toUpperCase?.() || "INR";

  const currencySymbols: Record<string, string> = {
    INR: "₹",
    AED: "د.إ",
    USD: "$",
  };
  const currencySymbol = currencySymbols[selectedCurrency] || selectedCurrency;

  const pickPrice = (plan: any, currency = "INR") => {
    const prices = Array.isArray(plan?.prices) ? plan.prices : [];
    return (
      prices.find((x: any) => x?.currency?.toUpperCase?.() === currency) ||
      prices[0]
    );
  };
  const remainingDays = nextBillingAt
    ? Math.max(
      0,
      Math.ceil(
        (new Date(nextBillingAt).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
      )
    )
    : 0;
  const breakdown = (count: number, plan: any) => {
    if (!plan || !count) {
      return { subtotal: 0, tax: 0, total: 0, unitPrice: 0, taxPercent: 0 };
    }

    const priceObj = pickPrice(plan, selectedCurrency);
    const unitPrice = Number(priceObj?.price ?? 0);
    const subtotal = count * unitPrice;

    const selectedOrgCountry = selectedOrgObj?.country?.toLowerCase().trim();
    const taxesList = (taxesResp as any)?.results || [];

    const countryMap: Record<string, string> = {
      "united arab emirates": "ae",
      "india": "in",
      "ae": "ae",
      "in": "in",
    };

    const matchedTax = taxesList.find((t: any) => {
      const taxCountry = t.country?.toLowerCase().trim();
      return (
        taxCountry === selectedOrgCountry ||
        taxCountry === countryMap[selectedOrgCountry || ""] ||
        countryMap[taxCountry || ""] === selectedOrgCountry
      );
    });

    const taxPercent = matchedTax ? Number(matchedTax?.rate ?? 0) : 0;
    const isInclusive = matchedTax ? !!matchedTax?.inclusive : !!priceObj?.tax?.inclusive;
    const taxName = matchedTax ? matchedTax.name : (priceObj?.tax?.name || "Tax");

    // Note: The previous logic checked priceObj?.tax?.type.
    // Now we rely on the matched global tax record.

    let tax = 0;
    let total = subtotal;

    if (taxPercent > 0) {
      if (!isInclusive) {
        // Exclusive Tax
        tax = subtotal * (taxPercent / 100);
        total = subtotal + tax;
      } else {
        // Inclusive Tax
        // If price is inclusive of tax:
        // Price = Base + Tax
        // Price = Base + (Base * Rate/100) = Base * (1 + Rate/100)
        // Base = Price / (1 + Rate/100)
        // Tax = Price - Base
        const base = subtotal / (1 + taxPercent / 100);
        tax = subtotal - base;
        total = subtotal;
      }
    }

    return { subtotal, tax, total, unitPrice, taxPercent, taxName, isInclusive };
  };

  const activeBD = breakdown(licenseCount, activePlan);

  const subTotal = activeBD.subtotal;
  const taxes = activeBD.tax;
  const grandTotal = activeBD.total;

  const handleSubscribe = async () => {


    const products: { plan: string; quantity: number }[] = [];

    if (licenseCount > 0 && activePlan?._id) {
      products.push({ plan: activePlan._id, quantity: licenseCount });
    }

    if (!selectedOrgId || products.length === 0) return;

    try {
      const resp = await addLicenses({
        organisationId: selectedOrgId,
        products,
        planId: activePlan?._id,
        quantity: licenseCount,
        currentBilledAmount: Number(grandTotal.toFixed(2)),
        currentBilledTaxAmount: Number(taxes.toFixed(2)),
      }).unwrap();

      if (isRazorpay) {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          console.error("Razorpay SDK failed to load.");
          return;
        }

        const subId =
          resp?.result?.subscription_id ||
          resp?.result?.subscriptionId ||
          resp?.result?.id ||
          resp?.result?.invoice?.id;

        const options = {
          key: import.meta.env.VITE_RAZORPAY_KEY_ID,
          subscription_id: subId,
          name: "GYMSTREAM PRIVATE LIMITED",
          image: "https://assets.gymstream.app/assets/site/fav-gymstream.png",
          description: "Activate Licenses",

          handler: function (response: any) {
            console.log("✅ Payment success", response);

            dispatch(
              subscriptionLicenseApi.util.invalidateTags([
                "SubscriptionLicenses",
                "SubscriptionScreens",
                "LicensesAddon",
              ])
            );

            onSuccess?.();   // 👈 tell parent
            handleModalClose(); // close child modal
          },

          modal: {
            ondismiss: function () {
              console.log("❌ Razorpay closed");

              onPaymentFailed?.(); // 👈 tell parent
              handleModalClose();  // close child modal
            },
          },

          theme: {
            color: "#2E2E2E",
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        onSuccess?.();
        if (resp?.result?.invoice?.invoiceUrl) {
          window.open(resp.result.invoice.invoiceUrl, "_blank");
          handleModalClose();
        } else {
          handleModalClose();
        }
      }
    } catch (err) {
      console.log("Subscription error", err);

      // ❌ API failure also trigger popup
      onPaymentFailed?.();
    }
  };

  if (!isOpen) return null;

  const isSubscribeDisabled =
    adding || !selectedOrgId || licenseCount === 0 || !activePlan;

  return (
    <div
      className="fixed inset-0 z-50 flex items-right justify-end bg-black/40"
      onClick={handleModalClose}
    >
      <div
        className="w-80 h-full rounded-[4px] bg-black shadow-[0_0_25px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-80 h-full ${isDark ? "bg-[#1B1B1B]" : "bg-white"}`}>
          {/* Header */}
          <div
            className={`flex items-center justify-between px-4 py-2 mb-2 border-b ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
              }`}
          >
            <h3
              className={`text-[15px] font-semibold ${isDark ? "text-white" : "text-gray-900"
                }`}
            >
              Add New License
            </h3>
            <button
              onClick={handleModalClose}
              className={`w-6 h-6 flex items-center justify-center rounded-[7.529px] transition-opacity ${isDark
                ? "bg-[#2E2E2E] shadow-[0_-0.941px_0.941px_0_rgba(150,161,172,0.12)_inset] hover:opacity-80"
                : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M12.4235 2.63525L2.63525 12.4235M2.63525 2.63525L12.4235 12.4235"
                  stroke={isDark ? "#F3F3F6" : "#4B5563"}
                  strokeWidth="1.12942"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-4 py-3 space-y-4 text-[11px] leading-relaxed">
            {/* Choose Organization */}
            <div className="space-y-1">
              <div
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Choose Organization
              </div>
              <select
                className={`w-full h-7 px-2 rounded-sm border text-[11px] focus:outline-none focus:border-[#26A7FF] ${isDark
                  ? "border-[#3A3A3A] bg-[#101010] text-gray-100"
                  : "border-gray-300 bg-white text-gray-900"
                  }`}
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                disabled={options.length === 0}
              >
                {options.length === 0 && (
                  <option>No organizations available</option>
                )}
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Cost by */}
            <div className="space-y-1 w-full flex flex-col">
              <div
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Cost by
              </div>
              <div
                className={`flex w-full rounded-full border p-[2px] ${isDark
                  ? "border-[#3A3A3A] bg-[#101010]"
                  : "border-gray-300 bg-gray-100"
                  }`}
              >
                <button
                  type="button"
                  onClick={() => setBillingCycle(PlanBillingCycle.MONTHLY)}
                  className={`flex-1 text-center px-3 py-[3px] rounded-full text-[10px] font-medium transition-colors ${billingCycle === PlanBillingCycle.MONTHLY
                    ? "bg-[#C6D92C] text-black"
                    : "text-gray-300"
                    }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle(PlanBillingCycle.ANNUALLY)}
                  className={`flex-1 text-center px-3 py-[3px] rounded-full text-[10px] font-medium transition-colors ${billingCycle === PlanBillingCycle.ANNUALLY
                    ? "bg-[#C6D92C] text-black"
                    : "text-gray-300"
                    }`}
                >
                  Annual
                </button>

              </div>
            </div>

            {/* Choose License Type */}
            <div className="space-y-1 ">
              <div
                className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                  }`}
              >
                Choose License Type
              </div>
              <select
                className={`w-full h-7 px-2 rounded-sm border text-[11px] focus:outline-none focus:border-[#26A7FF] ${isDark
                  ? "border-[#3A3A3A] bg-[#101010] text-gray-100"
                  : "border-gray-300 bg-white text-gray-900"
                  }`}
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value as LicensesType)}
              >
                <option value={LicensesType.BASIC} disabled={!basicPlan}>
                  Basic
                </option>
                <option
                  value={LicensesType.PREMIUM}
                  disabled={!premiumPlan || import.meta.env.VITE_DISABLE_PREMIUM_PLANS === "YES"}
                >
                  {import.meta.env.VITE_DISABLE_PREMIUM_PLANS === "YES" ? "Premium (Unavailable)" : "Premium"}
                </option>
              </select>
            </div>

            {/* No. Licenses */}
            {!isRazorpay && (
              <div className="space-y-1">
                <div
                  className={`text-[12px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"
                    }`}
                >
                  No. Licenses
                </div>
                <div
                  className={`flex items-center justify-between h-8 rounded-full border px-2 ${isDark
                    ? "border-[#3A3A3A] bg-[#101010]"
                    : "border-gray-300 bg-gray-100"
                    }`}
                >
                  <button
                    type="button"
                    onClick={decrementCount}
                    className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs leading-none ${isDark
                      ? "border-[#3A3A3A] text-gray-300 hover:bg-white/10"
                      : "border-gray-400 text-gray-700 hover:bg-black/5"
                      }`}
                  >
                    <span>-</span>
                  </button>
                  <span
                    className={`text-xs font-semibold tracking-widest ${isDark ? "text-gray-100" : "text-gray-900"
                      }`}
                  >
                    {licenseCount.toString().padStart(2, "0")}
                  </span>
                  <button
                    type="button"
                    onClick={incrementCount}
                    className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs leading-none ${isDark
                      ? "border-[#3A3A3A] text-gray-300 hover:bg-white/10"
                      : "border-gray-400 text-gray-700 hover:bg-black/5"
                      }`}
                  >
                    <span>+</span>
                  </button>
                </div>
              </div>
            )}

            {/* Cost Details */}
            <div className="pt-1">
              <div
                className={`text-[12px] font-medium mb-2 border-b ${isDark
                  ? "text-gray-300 border-[#3A3A3A]"
                  : "text-gray-800 border-gray-200"
                  }`}
              >
                Cost Details
              </div>
              <div className="space-y-1 text-[10px]">
                <div
                  className={`flex justify-between pb-1 ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <span>No. Screens</span>
                  <span>Cost {currencySymbol}</span>
                </div>
                <div
                  className={`flex justify-between mt-1 pt-1 border-b ${isDark
                    ? "text-gray-100 border-[#3A3A3A]"
                    : "text-gray-900 border-gray-200"
                    }`}
                >
                  <span>{licenseCount}</span>
                  <span>{subTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between pt-2 border-b ${isDark
                    ? "text-gray-400 border-[#3A3A3A]"
                    : "text-gray-500 border-gray-200"
                    }`}
                >
                  <span>Due Total</span>
                  <span>{subTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <span>
                    Tax -{activeBD.taxName}{" "}
                    {activeBD.taxPercent > 0
                      ? `(${activeBD.taxPercent}% ${activeBD.isInclusive ? "inc." : "exc."
                      })`
                      : ""}
                  </span>
                  <span>{taxes.toFixed(2)}</span>
                </div>
                <div
                  className={`flex justify-between font-semibold ${isDark ? "text-gray-100" : "text-gray-900"
                    }`}
                >
                  <span>Total</span>
                  <span>{grandTotal.toFixed(2)}</span>
                </div>
                <div
                  className={`pt-2 text-[10px] leading-snug flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="flex-shrink-0"
                  >
                    <path
                      d="M7.00033 12.8334C10.222 12.8334 12.8337 10.2217 12.8337 7.00008C12.8337 3.77842 10.222 1.16675 7.00033 1.16675C3.77866 1.16675 1.16699 3.77842 1.16699 7.00008C1.16699 10.2217 3.77866 12.8334 7.00033 12.8334Z"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                    <path d="M7 9.33325V6.70825" stroke="#C6D92C" strokeWidth="1.5" />
                    <path d="M7 4.67356V4.66772" stroke="#C6D92C" strokeWidth="1.8" />
                  </svg>

                  <div className="flex flex-col gap-1">

                    {message && <span>{message}</span>}
                    {/* {hasActiveSubscription && remainingDays > 0 && (
                      <span>
                        You’ll be charged only for the remaining {remainingDays} days in this billing cycle.
                      </span>
                    )} */}

                    <span>
                      Future invoices will be billed on a monthly basis. The current amount reflects charges for the remaining days.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div
            className={`px-4 pb-3 pt-4 space-y-2 mt-4 border-t ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
              }`}
          >
            <button
              className={`w-full h-10 text-[11px] font-semibold text-black rounded-[10px] border mb-1 ${isDark ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]" : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                }`}
              style={{
                border: "1px solid var(--menu-active-border, #C6D92C)",
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
              onClick={() => {
                if (isRazorpay) {
                  // Direct subscribe, skips confirmation modal
                  handleSubscribe();
                } else if (needsCardFirst) {
                  navigate(adminRoutes.paymentList);
                } else {
                  setShowConfirm(true);
                }
              }}
              disabled={
                // If it doesn't need card first (like Razorpay) or if card exists, obey base subscribe disabled logic
                (!needsCardFirst) && isSubscribeDisabled
              }
            >
              {needsCardFirst
                ? "Add Your First Card"
                : adding
                  ? "Subscribing..."
                  : "Subscribe"}
            </button>
            <button
              onClick={handleModalClose}
              className={`w-full h-10 text-[11px] font-medium rounded-[10px] border ${isDark
                ? "border-[#3A3A3A] text-gray-300 hover:bg-white/5"
                : "border-gray-300 text-gray-700 hover:bg-black/5"
                }`}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>




      {/* Confirmation Modal */}
      <ConfirmSubscriptionModal
        isOpen={showConfirm}
        onConfirm={async () => {
          await handleSubscribe();
          setShowConfirm(false);
        }}
        onClose={() => setShowConfirm(false)}
        disabled={isSubscribeDisabled || adding}
        message={`This will activate ${licenseCount} ${String(licenseType || LicensesType.BASIC).toUpperCase().includes("PREMIUM") ? "Premium" : "Basic"
          } license(s) for ${selectedOrgObj?.displayName || selectedOrgObj?.name || "the selected organization"
          } and start recurring billing. You can cancel anytime.`}
      />
    </div>
  );
};

export default AddLicensesModal;
